(() => {
  const ROOT_SELECTOR = '[data-coven-pair]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const initialize = (root) => {
    if (!root || root.dataset.covenPairInitialized === 'true') return;
    root.dataset.covenPairInitialized = 'true';

    const hero = root.querySelector('[data-coven-pair-hero]');
    const productForm = root.closest('product-form-component');
    const sticky = root.querySelector('[data-coven-pair-sticky]');
    const stickyButton = sticky?.querySelector('button');
    const footer = document.querySelector('footer');
    const scroller = root.closest('.page-wrapper') || window;
    const mobileMedia = window.matchMedia(MOBILE_QUERY);
    const reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
    const cleanup = [];

    const setStickyState = (visible) => {
      if (!sticky || !stickyButton) return;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      stickyButton.setAttribute('tabindex', visible ? '0' : '-1');
      document.body.classList.toggle('coven-pair-sticky-visible', visible);
    };

    const setHeroChatState = (heroVisible) => {
      document.body.classList.toggle('coven-pair-hero-active', heroVisible);
    };

    const updateSticky = () => {
      if (!hero || !sticky || !stickyButton || !mobileMedia.matches) {
        setStickyState(false);
        setHeroChatState(false);
        return;
      }

      const headerHeight = Number.parseFloat(
        getComputedStyle(document.body).getPropertyValue('--header-height')
      ) || 0;
      const heroPassed = hero.getBoundingClientRect().bottom <= headerHeight + 24;
      const footerApproaching = footer
        ? footer.getBoundingClientRect().top <= window.innerHeight - sticky.offsetHeight
        : false;

      setHeroChatState(!heroPassed);
      setStickyState(heroPassed && !footerApproaching);
    };

    const listen = (target, eventName, handler, options) => {
      target.addEventListener(eventName, handler, options);
      cleanup.push(() => target.removeEventListener(eventName, handler, options));
    };

    const revealElements = Array.from(root.querySelectorAll('.coven-pair__reveal'));
    if (
      root.classList.contains('coven-pair--motion-enabled') &&
      !reducedMotionMedia.matches &&
      'IntersectionObserver' in window
    ) {
      root.classList.add('coven-pair--motion-ready');
      const revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { root: null, rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      revealElements.forEach((element) => revealObserver.observe(element));
      cleanup.push(() => revealObserver.disconnect());
    } else {
      revealElements.forEach((element) => element.classList.add('is-visible'));
    }

    let frame = 0;
    const scheduleStickyUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateSticky();
      });
    };

    listen(scroller, 'scroll', scheduleStickyUpdate, { passive: true });
    if (scroller !== window) listen(window, 'scroll', scheduleStickyUpdate, { passive: true });
    listen(window, 'resize', scheduleStickyUpdate, { passive: true });
    listen(window, 'orientationchange', scheduleStickyUpdate, { passive: true });

    if ('addEventListener' in mobileMedia) {
      listen(mobileMedia, 'change', scheduleStickyUpdate);
      listen(reducedMotionMedia, 'change', () => {
        if (reducedMotionMedia.matches) {
          root.classList.remove('coven-pair--motion-ready');
          revealElements.forEach((element) => element.classList.add('is-visible'));
        }
      });
    }

    updateSticky();

    root.covenPairCleanup = () => {
      if (frame) window.cancelAnimationFrame(frame);
      cleanup.forEach((callback) => callback());
      setStickyState(false);
      setHeroChatState(false);
      root.removeAttribute('data-coven-pair-initialized');
    };
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR);
    root?.covenPairCleanup?.();
  });
})();
